import cloneDeep from "lodash.clonedeep";
import set from "lodash.set";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { ACTIONS, ROUTES } from "../../constants";
import ajv from "../../helpers/ajv";
import { resolveSchema } from "../../helpers/resolveSchema";
import Button from "../components/Button";
import Form from "../components/Form";
import Modal from "../components/Modal";
import mapChildren from "../helpers/mapChildren";
import { useContractQuery } from "../hooks";
import { useMutateContractQuery } from "../hooks/useMutateContractQuery";
import { useMutation } from "react-query";
import { compile } from "path-to-regexp";
import { v4 as uuid } from "uuid";
import { useHistory } from "react-router-dom";

const CancelAndReplaceForm = ({ handleClose, schemaData, contractData, setIsCreating }) => {
  const { createContract, updateContract, contractId, endorsementId, productRef, isCreating } =
    useMutateContractQuery();

  const { push } = useHistory();

  const schemaRef = schemaData?.["$id"];

  useEffect(() => {
    setIsCreating(isCreating);
  }, [isCreating]);

  const initialFormValues = {
    cancelAndReplaceTo: "",
    cancellation: {
      cancellation_date: new Date().toISOString(),
    },
    transit: {
      refund_transit_premium: {
        amount: contractData?.boundQuote?.rates?.basePremium?.netPremium,
        currency: "USD",
      },
      refund_transit_premium_tax: {
        amount: 0,
        currency: "USD",
      },
    },
    exhibitions: {
      refund_exhibitions_premium: {
        amount: contractData?.boundQuote?.rates?.exhibitionsPremium?.netPremium,
        currency: "USD",
      },
      refund_exhibitions_premium_tax: {
        amount: 0,
        currency: "USD",
      },
      ...contractData?.submission?.exhibitions,
    },
  };

  const [formValues, setFormValues] = useState(initialFormValues);
  const clonedSchema = cloneDeep(schemaData);
  const resolvedSchema = resolveSchema(clonedSchema, formValues);
  const clonedValues = cloneDeep(formValues);
  const validate = ajv.compile(schemaData);
  const isValid = validate(clonedValues);

  useEffect(() => {
    const initialTaxValue = contractData?.boundQuote?.rates?.basePremium?.tax;
    const taxValue = formValues?.transit?.refund_transit_premium_tax?.amount;

    if (
      initialTaxValue !== undefined &&
      taxValue !== undefined &&
      ((schemaRef === "cargo-us-single" && initialTaxValue > 0) ||
        (schemaRef === "cargo-us-annual" && initialTaxValue >= 0)) &&
      taxValue > initialTaxValue
    ) {
      const clonedFormValues = cloneDeep(formValues);
      set(clonedFormValues, `transit.refund_transit_premium_tax.amount`, initialTaxValue);
      setFormValues(clonedFormValues);
    }
  }, [formValues?.transit?.refund_transit_premium_tax]);

  useEffect(() => {
    const initialTaxValue = contractData?.boundQuote?.rates?.exhibitionsPremium?.tax;
    const taxValue = formValues?.exhibitions?.refund_exhibitions_premium_tax?.amount;

    if (
      initialTaxValue !== undefined &&
      taxValue !== undefined &&
      ((schemaRef === "cargo-us-single" && initialTaxValue > 0) ||
        (schemaRef === "cargo-us-annual" && initialTaxValue >= 0)) &&
      taxValue > initialTaxValue
    ) {
      const clonedFormValues = cloneDeep(formValues);
      set(clonedFormValues, `exhibitions.refund_exhibitions_premium_tax.amount`, initialTaxValue);
      setFormValues(clonedFormValues);
    }
  }, [formValues?.exhibitions?.refund_exhibitions_premium_tax]);

  const handleChange = (...args) => {
    const clonedFormValues = cloneDeep(formValues);

    if (args.length === 1) {
      const [event] = args;
      set(clonedFormValues, event.target.name, event.target.value);
    }

    if (args.length === 2) {
      const [value, name] = args;
      set(clonedFormValues, name, value);
    }

    setFormValues(clonedFormValues);
  };

  const createCancelAndReplaceFunc = async () => {
    const newContractId = uuid().toString();

    await createContract({
      productRef,
      contractId,
      data: { type: ACTIONS.CANCEL_ENDORSEMENT, payload: { ...formValues, cancelAndReplaceTo: newContractId } },
    });

    await updateContract({
      productRef,
      contractId,
      endorsementId,
      data: { type: ACTIONS.CANCEL_AND_REPLACE, payload: { newContractId } },
    });

    return newContractId;
  };

  const createCancelAndReplaceMutation = useMutation(() => createCancelAndReplaceFunc(), {
    onSuccess: (newContractId) => {
      handleClose();
      return push(compile(ROUTES.CONTRACT_EDIT)({ productRef, contractId: newContractId }));
    },
  });

  const handleSubmit = async () => {
    createCancelAndReplaceMutation.mutate();
  };

  return (
    <div className="p-6">
      <Form onSubmit={handleSubmit}>
        {mapChildren({
          formValues,
          onChange: handleChange,
          parentKey: "",
          parentSchema: resolvedSchema,
          setFormValues,
          validationErrors: validate.errors,
          ctx: { contractData },
        })}

        <div className="flex">
          <Button className="mr-4" isDisabled={!isValid || createCancelAndReplaceMutation.isLoading} kind="primary" type="submit">
            Submit
          </Button>

          <Button onClick={handleClose}>Cancel</Button>
        </div>
      </Form>
    </div>
  );
};

const CancelAndReplaceModal = ({ handleClose }) => {
  const { contractData, isLoading, schemaData } = useContractQuery();
  const [isCreating, setIsCreating] = useState(false);

  return (
    <Modal
      handleClose={handleClose}
      headingText={!isLoading && `Cancel & Replace policy - #${contractData.ref}`}
      isLoading={isLoading || isCreating}
    >
      <CancelAndReplaceForm
        contractData={contractData}
        handleClose={handleClose}
        schemaData={schemaData.properties.CancelAndReplaceForm}
        setIsCreating={setIsCreating}
      />
    </Modal>
  );
};

CancelAndReplaceModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
};

export default CancelAndReplaceModal;
